import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Toast({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="fixed top-5 right-5 z-50 flex flex-col gap-3 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-start gap-3 rounded-2xl px-5 py-4 shadow-lg text-sm font-medium max-w-sm transition-all ${
            t.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {t.type === 'success' ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" />
          ) : (
            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" />
          )}
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => onDismiss(t.id)}
            className="ml-2 opacity-50 hover:opacity-100 transition-opacity text-base leading-none"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

let toastCounter = 0;

export default function ProviderVerification() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [providers, setProviders] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState({});
  const [confirmPending, setConfirmPending] = useState({});
  const [rejectOpen, setRejectOpen] = useState({});
  const [rejectReasons, setRejectReasons] = useState({});
  const [actionErrors, setActionErrors] = useState({});
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const setProviderLoading = (id, state) => {
    setActionLoading((prev) => ({ ...prev, [id]: state }));
  };

  const setProviderError = (id, msg) => {
    setActionErrors((prev) => ({ ...prev, [id]: msg }));
  };

  const clearProviderError = (id) => {
    setActionErrors((prev) => ({ ...prev, [id]: null }));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/admin/providers/pending`,
          { credentials: 'include' }
        );

        if (res.status === 401) {
          navigate('/login');
          return;
        }

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Error ${res.status}`);
        }

        const data = await res.json();
        setProviders(data);
      } catch (err) {
        setFetchError(err.message || t('admin.errLoadProviders'));
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [navigate, t]);

  const requestVerify = (id) => {
    clearProviderError(id);
    setRejectOpen((prev) => ({ ...prev, [id]: false }));
    setConfirmPending((prev) => ({ ...prev, [id]: true }));
  };

  const cancelVerify = (id) => {
    setConfirmPending((prev) => ({ ...prev, [id]: false }));
  };

  const confirmVerify = async (provider) => {
    const id = provider.user_id;
    clearProviderError(id);
    setConfirmPending((prev) => ({ ...prev, [id]: false }));
    setProviderLoading(id, 'verifying');

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/providers/${id}/verify`,
        { method: 'POST', credentials: 'include' }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Error ${res.status}`);
      }

      setProviders((prev) => prev.filter((p) => p.user_id !== id));
      addToast(t('admin.providerVerifiedOk', { name: provider.name }), 'success');
    } catch (err) {
      setProviderError(id, err.message || t('admin.errorVerifying', { name: provider.name }));
      addToast(t('admin.errorVerifying', { name: provider.name }), 'error');
    } finally {
      setProviderLoading(id, null);
    }
  };

  const toggleRejectForm = (id) => {
    clearProviderError(id);
    setConfirmPending((prev) => ({ ...prev, [id]: false }));
    setRejectOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleRejectReasonChange = (id, value) => {
    setRejectReasons((prev) => ({ ...prev, [id]: value }));
  };

  const submitReject = async (provider) => {
    const id = provider.user_id;
    const reason = (rejectReasons[id] || '').trim();

    if (!reason) {
      setProviderError(id, t('admin.noReason'));
      return;
    }

    clearProviderError(id);
    setProviderLoading(id, 'rejecting');

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/providers/${id}/reject`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );

      if (res.status === 404) {
        throw new Error(t('admin.endpointMissing'));
      }

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Error ${res.status}`);
      }

      setProviders((prev) => prev.filter((p) => p.user_id !== id));
      addToast(t('admin.providerRejectedOk', { name: provider.name }), 'success');
    } catch (err) {
      setProviderError(id, err.message || t('admin.errorRejecting', { name: provider.name }));
      addToast(t('admin.errorRejecting', { name: provider.name }), 'error');
    } finally {
      setProviderLoading(id, null);
    }
  };

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  const formatDate = (dateStr) => {
    if (!dateStr) return t('admin.noDate');
    try {
      return new Date(dateStr).toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return t('admin.noDate');
    }
  };

  const roleLabel = (role) => {
    if (!role) return null;
    const map = {
      provider: t('admin.roleProvider2'),
      admin: t('admin.roleAdmin2'),
      user: t('admin.roleUser2'),
    };
    return map[role] || role;
  };

  if (initialLoading) {
    return <AdminLoadingScreen message={t('admin.loadingVerifications', 'Cargando verificaciones de proveedores...')} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />
      <Toast toasts={toasts} onDismiss={dismissToast} />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="mb-8 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition-colors"
        >
          {t('admin.backToPanelShort')}
        </button>

        <h1 className="text-4xl font-heading font-bold mb-2 text-[#134168]">
          {t('admin.verifProvidersTitle')}
        </h1>
        <p className="text-gray-500 mb-8 text-sm">
          {t('admin.verifPending', { count: providers.length })}
        </p>

        {fetchError && (
          <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{t('admin.errLoadProviders')}</p>
              <p className="text-sm mt-0.5">{fetchError}</p>
            </div>
          </div>
        )}

        {!fetchError && providers.length === 0 && (
          <div className="bg-white rounded-3xl p-12 shadow-sm text-center">
            <CheckCircle className="w-16 h-16 text-[#39b4aa] mx-auto mb-4" />
            <p className="text-gray-700 font-semibold text-lg">{t('admin.providerOk')}</p>
            <p className="text-gray-400 text-sm mt-1">{t('admin.noPendingProviders')}</p>
          </div>
        )}

        {providers.length > 0 && (
          <div className="grid grid-cols-1 gap-6">
            {providers.map((provider) => {
              const id = provider.user_id;
              const isVerifying = actionLoading[id] === 'verifying';
              const isRejecting = actionLoading[id] === 'rejecting';
              const isBusy = isVerifying || isRejecting;
              const showConfirm = !!confirmPending[id];
              const showRejectForm = !!rejectOpen[id];
              const cardError = actionErrors[id];
              const rejectReason = rejectReasons[id] || '';

              return (
                <div
                  key={id}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-16 h-16 bg-[#134168] rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      {(provider.name || '?')[0].toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg text-gray-900">
                          {provider.name || t('admin.noName')}
                        </h3>
                        {provider.role && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#134168]/10 text-[#134168]">
                            {roleLabel(provider.role)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {provider.email}
                      </p>
                      {provider.phone_whatsapp && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          WhatsApp: {provider.phone_whatsapp}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {t('admin.registeredAt')} {formatDate(provider.created_at)}
                      </p>
                    </div>

                    {!showConfirm && !showRejectForm && (
                      <div className="flex flex-wrap gap-3 sm:flex-nowrap">
                        <button
                          onClick={() => requestVerify(id)}
                          disabled={isBusy}
                          className="flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded-full font-bold hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {isVerifying ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                          {t('admin.verify')}
                        </button>

                        <button
                          onClick={() => toggleRejectForm(id)}
                          disabled={isBusy}
                          className="flex items-center gap-2 bg-white border border-red-200 text-red-600 px-5 py-2.5 rounded-full font-bold hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {isRejecting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          {t('admin.reject')}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Documento de identidad subido por el proveedor */}
                  <div className="mt-5 border-t border-gray-100 pt-5">
                    {provider.has_document ? (
                      <div className="flex flex-col sm:flex-row gap-4">
                        <a
                          href={`${BACKEND_URL}/api/admin/providers/${id}/document`}
                          target="_blank" rel="noopener noreferrer"
                          className="block shrink-0"
                        >
                          <img
                            src={`${BACKEND_URL}/api/admin/providers/${id}/document`}
                            alt="Documento de identidad"
                            className="w-44 h-28 object-cover rounded-xl border border-gray-200 hover:opacity-90 transition"
                            // Backend en subdominio distinto (api.*) — sin esto el navegador
                            // no manda la cookie de sesión httpOnly en el request de la imagen.
                            crossOrigin="use-credentials"
                          />
                        </a>
                        <div className="text-sm">
                          <p className="font-semibold text-gray-800">
                            {provider.document_type === 'pasaporte' ? 'Pasaporte' : 'Cédula'}
                          </p>
                          <p className="text-gray-600">N.º: {provider.document_id || '—'}</p>
                          <p className="text-xs text-gray-400 mt-1">Toca la imagen para verla en grande.</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">El proveedor aún no ha subido su documento de identidad.</p>
                    )}
                  </div>

                  {showConfirm && (
                    <div className="mt-5 border-t border-gray-100 pt-5">
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3 mb-4">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-800 font-medium">
                            <Trans i18nKey="admin.confirmVerifyDesc" values={{ name: provider.name }} />
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => confirmVerify(provider)}
                            disabled={isVerifying}
                            className="flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-full font-bold hover:bg-green-700 transition-colors disabled:opacity-60 text-sm"
                          >
                            {isVerifying && (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                            {t('admin.confirm')}
                          </button>
                          <button
                            onClick={() => cancelVerify(id)}
                            disabled={isVerifying}
                            className="px-5 py-2 rounded-full font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60 text-sm"
                          >
                            {t('admin.cancelBtn')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {showRejectForm && !showConfirm && (
                    <div className="mt-5 border-t border-gray-100 pt-5">
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-sm font-semibold text-red-800 mb-3">
                          <Trans i18nKey="admin.rejectReasonFor" values={{ name: provider.name }} />
                        </p>
                        <textarea
                          value={rejectReason}
                          onChange={(e) =>
                            handleRejectReasonChange(id, e.target.value)
                          }
                          placeholder={t('admin.describeReason')}
                          rows={3}
                          className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none bg-white placeholder-gray-400"
                        />
                        <div className="flex gap-3 mt-3">
                          <button
                            onClick={() => submitReject(provider)}
                            disabled={isRejecting || !rejectReason.trim()}
                            className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-full font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {isRejecting && (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                            {t('admin.sendReject')}
                          </button>
                          <button
                            onClick={() => toggleRejectForm(id)}
                            disabled={isRejecting}
                            className="px-5 py-2 rounded-full font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60 text-sm"
                          >
                            {t('admin.cancelBtn')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {cardError && (
                    <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                      <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{cardError}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
